
import { getAuthToken, setAuthToken, removeAuthToken, getAuthHeaders } from "@/utils/authUtils";

interface SignupData {
  name: string;
  email: string;
  password: string;
  phone: string;
  smsConsent: boolean;
}

interface LoginData {
  email: string;
  password: string;
}

interface BackendUser {
  user_id: number;
  name?: string;
  email: string;
  password?: string;
  phone_number?: string;
  subscription_tier?: 'free' | 'basic' | 'standard' | 'premium' | 'free_premium';
  notification_preferences?: any;
  created_at?: string;
  updated_at?: string;
  requires_password_change?: boolean;
  subscribed?: boolean;
  subscription_end?: string;
  subscription_canceled?: boolean;
}

interface AuthResponse {
  success?: boolean;
  user?: BackendUser;
  message?: string;
  token?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:10000';

export const authService = {
  async signup(data: SignupData): Promise<AuthResponse> {
    console.log('🚀 Signup attempt started');
    console.log('📤 Sending signup data:', {
      name: data.name,
      email: data.email,
      phone: data.phone,
      smsConsent: data.smsConsent,
      passwordProvided: !!data.password
    });
    console.log('🌐 API URL:', `${API_BASE_URL}/api/auth/signup`);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          password: data.password,
          phone_number: data.phone,
          notification_preferences: {
            sms_consent: data.smsConsent
          },
          alert_preferences: {
            new_items: true,
            back_in_stock: true,
            price: true,
            monthly_allocated: true,
            lottery: true,
            notification_methods: ["sms"]
          }
        }),
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      const result = await response.json();
      console.log('📥 Response data:', result);
      
      if (!response.ok) {
        // Use the actual server error message - this is the key improvement
        const errorMessage = result.message || result.error || `HTTP ${response.status}: ${response.statusText}`;
        console.error('❌ Signup failed:', errorMessage);
        
        // Throw the actual server message instead of generic ones
        throw new Error(errorMessage);
      }

      // Store the JWT token if provided with verification
      if (result.token) {
        await setAuthToken(result.token);
        console.log('🔑 JWT token stored successfully');
        
        // Additional verification after a longer delay
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const verifyToken = getAuthToken();
        console.log('🔍 Token verification after signup:', verifyToken ? 'Available' : 'Missing');
        
        if (!verifyToken) {
          throw new Error('Failed to store authentication token');
        }
      }

      console.log('✅ Signup successful');
      return result;
    } catch (error) {
      console.error('🔥 Signup error:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('🌐 Network error - check if backend is running');
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }
  },

  async login(data: LoginData): Promise<AuthResponse> {
    console.log('🚀 Login attempt started for:', data.email);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      console.log('📡 Login response status:', response.status);

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();
      console.log('📥 Login response data:', result);
      
      if (!response.ok) {
        // Use the actual server error message
        const errorMessage = result.message || result.error || 'Login failed';
        throw new Error(errorMessage);
      }

      // Store the JWT token if provided with verification
      if (result.token) {
        await setAuthToken(result.token);
        console.log('🔑 JWT token stored successfully');
        
        // Additional verification after a longer delay
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const verifyToken = getAuthToken();
        console.log('🔍 Token verification after login:', verifyToken ? 'Available' : 'Missing');
        
        if (!verifyToken) {
          throw new Error('Failed to store authentication token');
        }
      }

      // For new users or users without alert preferences, set up defaults
      if (result.user && result.success) {
        await this.ensureDefaultAlertPreferences(result.user.user_id);
      }

      console.log('✅ Login successful');
      return result;
    } catch (error) {
      console.error('🔥 Login error:', error);
      throw error;
    }
  },

  async logout(): Promise<void> {
    try {
      // Remove the stored token
      removeAuthToken();
      console.log('🔑 JWT token removed');
      
      // Optionally call backend logout endpoint if it exists
      const token = getAuthToken();
      if (token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Don't throw - we still want to clear local storage
    }
  },

  async checkAuth(): Promise<AuthResponse> {
    console.log('🔍 Checking authentication status...');
    
    try {
      const token = getAuthToken();
      if (!token) {
        console.log('❌ No token found, user not authenticated');
        return { success: false };
      }

      console.log('🔑 Token found, making request to /api/auth/me');
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      console.log('📡 Auth check response status:', response.status);

      if (!response.ok) {
        console.log('❌ Auth check failed, removing invalid token');
        removeAuthToken();
        return { success: false };
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.log('❌ Non-JSON response from auth check');
        return { success: false };
      }

      const user = await response.json();
      console.log('✅ Auth check successful, user data:', user);
      
      return { success: true, user };
    } catch (error) {
      console.error('🔥 Auth check error:', error);
      // Remove potentially invalid token
      removeAuthToken();
      return { success: false };
    }
  },

  async updateUser(userId: string, updates: Partial<BackendUser>): Promise<BackendUser> {
    console.log('🔄 Updating user:', userId, 'with:', updates);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      console.log('📡 Update user response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ User update failed:', errorText);
        throw new Error(`Failed to update user: ${response.status} ${errorText}`);
      }

      const updatedUser = await response.json();
      console.log('✅ User updated successfully:', updatedUser);
      
      return updatedUser;
    } catch (error) {
      console.error('🔥 Error updating user:', error);
      throw error;
    }
  },

  async ensureDefaultAlertPreferences(userId: number): Promise<void> {
    console.log('🔧 Ensuring default alert preferences for user:', userId);
    
    try {
      // Check if user already has alert preferences
      const checkResponse = await fetch(`${API_BASE_URL}/api/alert-preferences`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (checkResponse.ok) {
        const existingPrefs = await checkResponse.json();
        if (existingPrefs.preferences || existingPrefs.price_changes !== undefined) {
          console.log('✅ User already has alert preferences, skipping setup');
          return;
        }
      }

      // Set up default alert preferences
      const defaultPreferences = {
        preferences: {
          new_items: true,
          back_in_stock: true,
          price: true,
          monthly_allocated: true,
          lottery: true,
          price_threshold: 10,
          notification_methods: ["sms"],
          price_change_types: {
            increases: true,
            decreases: true
          },
          minimum_price_change_percent: 5
        }
      };

      const response = await fetch(`${API_BASE_URL}/api/alert-preferences`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(defaultPreferences),
      });

      if (response.ok) {
        console.log('✅ Default alert preferences set successfully');
      } else {
        console.log('⚠️ Failed to set default alert preferences, but continuing');
      }
    } catch (error) {
      console.error('🔥 Error setting default alert preferences:', error);
      // Don't throw - this shouldn't block login
    }
  }
};
